import { useContext, useEffect, useState } from "react";
import { addCoupon, updateCoupon } from "../../../components/coupons/functions";
import ToastContext from "../../../context/toastProvider";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileFormField from "../../ui/MobileFormField";

const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha disabled:bg-monao";
const primaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50";
const secondaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao disabled:opacity-50";

export function constrainCouponCode(value) {
  if (!value || value.length <= 8) {
    return value ?? "";
  }
  return value.substring(0, 8);
}

export function isCreateCouponFormValid(fields) {
  const code = fields.code?.trim() ?? "";
  return (
    code.length >= 5 &&
    code.length <= 8 &&
    Boolean(fields.description?.trim()) &&
    Boolean(fields.value?.toString().trim()) &&
    Boolean(fields.kind)
  );
}

export function hasCouponChanges(fields, original) {
  return (
    fields.code !== original.code ||
    fields.description !== original.description ||
    fields.value !== original.value ||
    fields.kind !== original.kind ||
    fields.isActive !== original.isActive
  );
}

function CouponKindFields({ kind, onChange }) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-laranjinha/80">Tipo</legend>
      <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80">
        <input
          type="radio"
          name="kind"
          value="percent"
          checked={kind === "percent"}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Percentual"
        />
        Percentual
      </label>
      <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80">
        <input
          type="radio"
          name="kind"
          value="amount"
          checked={kind === "amount"}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Quantia"
        />
        Quantia
      </label>
    </fieldset>
  );
}

export function MobileCreateCouponSheet({ open, onClose, user, onCreated }) {
  const { goodToast, badToast } = useContext(ToastContext);

  const [fields, setFields] = useState({
    code: "",
    description: "",
    value: "",
    kind: "",
  });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFields({ code: "", description: "", value: "", kind: "" });
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!fields.code.trim()) {
      return badToast("Insira um código!");
    }
    if (!fields.description.trim()) {
      return badToast("Insira uma descrição!");
    }
    if (!fields.value.toString().trim()) {
      return badToast("Insira um valor!");
    }
    if (!fields.kind) {
      return badToast("Determine o tipo!");
    }
    if (!isCreateCouponFormValid(fields)) {
      return badToast("Código deve ter entre 5 e 8 caracteres");
    }

    setLoading(true);
    const data = await addCoupon(user.token, {
      code: fields.code,
      description: fields.description,
      value: fields.value,
      kind: fields.kind,
    });
    setLoading(false);

    if (data.type === "success") {
      goodToast("Novo cupom cadastrado!");
      resetForm();
      onCreated();
    } else {
      badToast("Erro ao cadastrar cupom");
    }
  };

  if (!open) return null;

  const disableSubmit = loading || !isCreateCouponFormValid(fields);

  return (
    <MobileFilterSheet open={open} onClose={handleClose} title="Cadastrar cupom">
      <div className="space-y-4">
        <p className="text-sm text-laranjinha/70">
          O código deve ter entre 5 e 8 caracteres.
        </p>
        <MobileFormField label="Código">
          <input
            type="text"
            aria-label="Código"
            value={fields.code}
            onChange={(e) =>
              setFields((current) => ({
                ...current,
                code: constrainCouponCode(e.target.value),
              }))
            }
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Descrição">
          <input
            type="text"
            aria-label="Descrição"
            value={fields.description}
            onChange={(e) =>
              setFields((current) => ({ ...current, description: e.target.value }))
            }
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Valor">
          <input
            type="text"
            aria-label="Valor"
            value={fields.value}
            onChange={(e) =>
              setFields((current) => ({ ...current, value: e.target.value }))
            }
            className={inputClass}
          />
        </MobileFormField>
        <CouponKindFields
          kind={fields.kind}
          onChange={(kind) => setFields((current) => ({ ...current, kind }))}
        />
        <div className="flex gap-3">
          <button type="button" onClick={handleClose} className={secondaryBtn}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disableSubmit}
            className={primaryBtn}
          >
            Cadastrar
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}

export function MobileCouponDetailSheet({ open, onClose, coupon, user, onChanged }) {
  const { goodToast, badToast } = useContext(ToastContext);

  const [fields, setFields] = useState({
    code: "",
    description: "",
    value: "",
    kind: "",
    isActive: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!coupon) return;
    setFields({
      code: coupon.code ?? "",
      description: coupon.description ?? "",
      value: coupon.value ?? "",
      kind: coupon.kind ?? "",
      isActive: Boolean(coupon.isActive),
    });
  }, [coupon]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    if (!hasCouponChanges(fields, coupon)) {
      return;
    }

    setLoading(true);
    const data = await updateCoupon(user.token, coupon._id, {
      code: fields.code,
      description: fields.description,
      value: fields.value,
      kind: fields.kind,
      isActive: fields.isActive,
    });
    setLoading(false);

    if (data.type === "success") {
      goodToast("Cupom atualizado!");
      onChanged();
    } else {
      badToast("Erro ao atualizar cupom");
    }
  };

  if (!open || !coupon) return null;

  const disableSubmit =
    loading || !hasCouponChanges(fields, coupon);

  return (
    <MobileFilterSheet open={open} onClose={handleClose} title="Detalhe do cupom">
      <div className="space-y-4">
        <MobileFormField label="ID">
          <input
            type="text"
            value={coupon._id}
            disabled
            className={inputClass}
            aria-label="ID"
          />
        </MobileFormField>
        <MobileFormField label="Código">
          <input
            type="text"
            aria-label="Código"
            value={fields.code}
            onChange={(e) =>
              setFields((current) => ({ ...current, code: e.target.value }))
            }
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Descrição">
          <textarea
            rows={2}
            aria-label="Descrição"
            value={fields.description}
            onChange={(e) =>
              setFields((current) => ({ ...current, description: e.target.value }))
            }
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Valor">
          <input
            type="text"
            aria-label="Valor"
            value={fields.value}
            onChange={(e) =>
              setFields((current) => ({ ...current, value: e.target.value }))
            }
            className={inputClass}
          />
        </MobileFormField>
        <CouponKindFields
          kind={fields.kind}
          onChange={(kind) => setFields((current) => ({ ...current, kind }))}
        />
        <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80">
          <input
            type="checkbox"
            checked={fields.isActive}
            onChange={() =>
              setFields((current) => ({ ...current, isActive: !current.isActive }))
            }
            aria-label="Ativo"
          />
          Ativo
        </label>
        <div className="flex gap-3">
          <button type="button" onClick={handleClose} className={secondaryBtn}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disableSubmit}
            className={primaryBtn}
          >
            Salvar
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}
